import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { createMarkup } from "@zempler/utils";
import { LinkType } from "../../types";
import Typography from "../typography/Typography";
import Link from "../link/Link";
import Stack from "../stack/Stack";
import { useBackgroundColor, useResponsiveType } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";

// Interface

export interface StepsProps {
  steps?: StepProps[];
}

interface StepProps {
  heading: string;
  text?: string;
  link?: LinkType;
  backgroundColor: ThemeColorsKeys;
}

// Styles

const StyledSteps = styled.ol`
  ${({ theme: { media, borderRadii, borders } }) => css`
    border-radius: ${borderRadii.small};
    border: ${borders.darkBlue};
    overflow: hidden;
    counter-reset: steps-counter;

    @media (min-width: ${media.medium}) {
      display: grid;
      grid-template-columns: repeat(3, auto);
    }
  `};
`;

const StyledStep = styled.li`
  ${({ theme: { space, media, borders }, backgroundColor }) => css`
    padding: ${space.fluidMediumToLarge};
    counter-increment: steps-counter;
    display: flex;
    flex-direction: column;

    > div {
      flex-grow: 1;
      display: flex;
      flex-direction: column;

      div {
        flex-grow: 1;
      }
    }

    &:before {
      content: counter(steps-counter);
      ${useBackgroundColor("darkBlue")}
      ${useResponsiveType("headingSmall")}
      width: ${space.fluidLargeToXxLarge};
      height: ${space.fluidLargeToXxLarge};
      border-radius: 50%;
      display: grid;
      place-items: center;
      margin-bottom: ${space.fluidXSmallToMedium};
    }

    &:first-of-type {
      ${useBackgroundColor(backgroundColor || "lilac")}
      border-bottom: ${borders.darkBlue};
    }

    &:nth-of-type(2) {
      ${useBackgroundColor(backgroundColor || "white")}
      border-bottom: ${borders.darkBlue};
    }

    &:last-of-type {
      ${useBackgroundColor(backgroundColor || "zemplerBlue")}
    }

    @media (min-width: ${media.medium}) {
      &:first-of-type {
        border-bottom: none;
        border-right: ${borders.darkBlue};
      }

      &:nth-of-type(2) {
        border-bottom: none;
        border-right: ${borders.darkBlue};
      }
    }
  `};
`;

// JSX

const Step: FC<StepProps> = ({ heading, text, link, backgroundColor }) => (
  <StyledStep backgroundColor={backgroundColor}>
    <Stack gap="xSmall">
      {heading && (
        <Typography component="h3" variant="secondaryHeadingSmallBold">
          {heading}
        </Typography>
      )}
      {text && (
        <Typography component="div" variant="bodyToLarge">
          {createMarkup(text)}
        </Typography>
      )}
      {link && <Link {...link} />}
    </Stack>
  </StyledStep>
);

export const Steps: FC<StepsProps> = ({ steps }) => (
  <StyledSteps>
    {steps && steps.map((step, index) => <Step key={index} {...step} />)}
  </StyledSteps>
);

export default Steps;
